/**
 * `.TTM` (v3.38) TT3 bytecode opcode names (docs/formats/ttm-animation.md).
 *
 * A TT3 instruction's 16-bit code splits as `op = code & 0xFFF0`, `count =
 * code & 0x000F` (ttm-bytecode.ts). This table maps `op` — the masked
 * base opcode, which for a handful of families (`DRAW STRING n`, `SET
 * STRING n`, palette block-swap) already bakes a sub-index into its own
 * middle nibble — to a name.
 *
 * Sourced from ScummVM's `engines/dgds/ttm.cpp` `TTMInterpreter::ttmOpName`
 * (GPL-3.0-or-later, **behaviour reference only, names transcribed as
 * facts, no code copied** — see docs/formats/compression.md's licensing
 * table for this project's stance), which documents the later, ADS-capable
 * DGDS dialect (Rise of the Dragon and successors). Red Baron ships no
 * `.ADS`, so the sequencing/conditional opcodes in that table (0x1100
 * range's ADS-only cousins, `GOSUB`, the whole `0x13xx`/`0x2000`-range ADS
 * conditional family) never appear in our data — they're kept here only as
 * unobserved, REPORTED entries for completeness.
 *
 * Two opcodes were found in the real Red Baron corpus that **do not exist
 * anywhere in dgds's table** — confirmed by grepping the whole `dgds`
 * engine tree, not just this switch:
 *
 * - `0x10C0` — always takes one arg and always precedes a `0xF070` load,
 *   the same shape as `0x1090 SELECT SONG` before `0xF060 LOAD SONG` or
 *   `0x1060 SELECT PAL` before `0xF050 LOAD PAL`. Named `SELECT SND SLOT`
 *   by that analogy (REPORTED/INFERRED — Red Baron-only).
 * - `0xF070` — a string-arg load, observed loading `SIMSNDS.SNG` /
 *   `SHLSNDS2.SNG` (sound-effect banks). Named `LOAD SND/SX` (REPORTED —
 *   Red Baron-only; dgds's closest analogue is the differently-numbered
 *   `0xC210 LOAD RAW SFX`).
 *
 * Every *other* opcode observed in the 28-file corpus (docs/formats/
 * ttm-animation.md's census) matches a dgds table entry both in name and
 * in the arg-count/arg-shape actually used — see that doc's VERIFIED/
 * REPORTED markers for the full opcode-by-opcode account.
 */

/** `0xAF1F` — draw empty polygon: `count == 0x0F` but the payload is a point list, not a string. */
export const DRAW_EMPTY_POLY_OP = 0xaf10;
/** `0xAF2F` — draw filled polygon: `count == 0x0F` but the payload is a point list, not a string. */
export const DRAW_FILLED_POLY_OP = 0xaf20;

export const TTM_OPCODE_NAMES: ReadonlyMap<number, string> = new Map([
  [0x0000, "FINISH"],
  [0x0020, "SAVE BACKGROUND"],
  [0x0070, "FREE PALETTE"],
  [0x0080, "FREE SHAPE"],
  [0x0090, "FREE FONT"],
  [0x00b0, "NULLOP"],
  [0x00c0, "FREE BACKGROUND"],
  [0x0110, "PURGE"],
  [0x0210, "SONG SOMETHING"],
  [0x0220, "STOP CURRENT MUSIC"],
  [0x0230, "FADE CURRENT MUSIC"],
  [0x0400, "PALETTE RESET"],
  [0x0500, "FLIP MODE ON"],
  [0x0510, "FLIP MODE OFF"],
  [0x0ff0, "FINISH FRAME"],
  [0x1020, "SET DELAY"],
  [0x1030, "SET BRUSH"],
  [0x1050, "SELECT BMP"],
  [0x1060, "SELECT PAL"],
  [0x1070, "SELECT FONT"],
  [0x1090, "SELECT SONG"],
  [0x10a0, "SET SCENE"],
  [0x10c0, "SELECT SND SLOT"], // Red Baron-only, not in dgds — see module doc.
  [0x1100, "SET SCENE (SEQUENCE)"],
  [0x1110, "SET SCENE (SEQUENCE START)"],
  [0x1120, "SET GETPUT NUM"],
  [0x1200, "GOTO"],
  [0x1300, "PLAY SFX"],
  [0x1310, "STOP SFX"],
  [0x2000, "SET DRAW COLORS"],
  [0x2010, "SET FRAME"],
  [0x2020, "SET RANDOM DELAY"],
  [0x2030, "SET SCROLL OFFSET"],
  [0x2300, "PAL SET BLOCK SWAP 0"],
  [0x2310, "PAL SET BLOCK SWAP 1"],
  [0x2320, "PAL SET BLOCK SWAP 2"],
  [0x2400, "PAL DO BLOCK SWAP"],
  [0x3000, "GOSUB"],
  [0x3100, "SCROLL"],
  [0x3200, "CDS FIND TARGET"],
  [0x3300, "CDS GOSUB"],
  [0x4000, "SET CLIP WINDOW"],
  [0x4110, "FADE OUT"],
  [0x4120, "FADE IN"],
  [0x4200, "STORE AREA"],
  [0x4210, "SAVE GETPUT REGION"],
  [0x5000, "SET DYNAMIC RECT"],
  [0xa000, "DRAW PIXEL"],
  [0xa010, "WIPE DISSOLVE"],
  [0xa020, "WIPE 20"],
  [0xa030, "WIPE OUT-TO-IN"],
  [0xa040, "WIPE INTERLACED"],
  [0xa050, "WIPE LEFT-TO-RIGHT"],
  [0xa060, "WIPE RIGHT-TO-LEFT"],
  [0xa070, "WIPE TOP-TO-BOTTOM"],
  [0xa080, "WIPE BOTTOM-TO-TOP"],
  [0xa090, "WIPE IN-TO-OUT"],
  [0xa0a0, "DRAW LINE"],
  [0xa100, "DRAW FILLED RECT"],
  [0xa110, "DRAW EMPTY RECT"],
  [0xa200, "DRAW STRING 0"],
  [0xa210, "DRAW STRING 1"],
  [0xa220, "DRAW STRING 2"],
  [0xa230, "DRAW STRING 3"],
  [0xa240, "DRAW STRING 4"],
  [0xa250, "DRAW STRING 5"],
  [0xa260, "DRAW STRING 6"],
  [0xa270, "DRAW STRING 7"],
  [0xa280, "DRAW STRING 8"],
  [0xa290, "DRAW STRING 9"],
  [0xa300, "DRAW STRINGS AS DIALOG"],
  [0xa400, "DRAW FILLED CIRCLE"],
  [0xa420, "DRAW EMPTY CIRCLE"],
  [0xa500, "DRAW SPRITE"],
  [0xa510, "DRAW SPRITE FLIP-V"],
  [0xa520, "DRAW SPRITE FLIP-H"],
  [0xa530, "DRAW SPRITE FLIP-HV"],
  [0xa600, "DRAW GETPUT"],
  [0xa700, "DRAW SCROLL"],
  [DRAW_EMPTY_POLY_OP, "DRAW EMPTY POLY"],
  [DRAW_FILLED_POLY_OP, "DRAW FILLED POLY"],
  [0xaf00, "FLOOD FILL"],
  [0xb000, "INIT CREDITS SCROLL"],
  [0xb010, "DRAW CREDITS SCROLL"],
  [0xb600, "COPY BUFFER"],
  [0xc020, "LOAD SAMPLE"],
  [0xc030, "SELECT SAMPLE"],
  [0xc040, "DESELECT SAMPLE"],
  [0xc050, "PLAY SAMPLE"],
  [0xc060, "STOP SAMPLE"],
  [0xc070, "PAUSE SAMPLE"],
  [0xc080, "UNPAUSE SAMPLE"],
  [0xc090, "MUTE SAMPLE"],
  [0xc0a0, "UNMUTE SAMPLE"],
  [0xc0b0, "SAMPLE PRIORITY"],
  [0xc0c0, "SAMPLE HOLD"],
  [0xc0d0, "SAMPLE BEND"],
  [0xc0e0, "FADE SONG"],
  [0xc0f0, "SONG CONTROLLER"],
  [0xc100, "SAMPLE VOL"],
  [0xc200, "RAW SFX"],
  [0xc210, "LOAD RAW SFX"],
  [0xc220, "PLAY RAW SFX"],
  [0xc240, "STOP RAW SFX"],
  [0xc250, "SYNC RAW SFX"],
  [0xcf10, "SFX MASTER VOL"],
  [0xf010, "LOAD SCR"],
  [0xf020, "LOAD BMP"],
  [0xf040, "LOAD FONT"],
  [0xf050, "LOAD PAL"],
  [0xf060, "LOAD SONG"],
  [0xf070, "LOAD SND/SX"], // Red Baron-only, not in dgds — see module doc.
  [0xf080, "LOAD SCROLL"],
  [0xf100, "SET STRING 0"],
  [0xf110, "SET STRING 1"],
  [0xf120, "SET STRING 2"],
  [0xf130, "SET STRING 3"],
  [0xf140, "SET STRING 4"],
  [0xf150, "SET STRING 5"],
  [0xf160, "SET STRING 6"],
  [0xf170, "SET STRING 7"],
  [0xf180, "SET STRING 8"],
  [0xf190, "SET STRING 9"],
]);

/** Looks up the name for a *masked* base opcode (`code & 0xFFF0`), or `undefined` if not in the table. */
export function ttmOpcodeName(op: number): string | undefined {
  return TTM_OPCODE_NAMES.get(op);
}
